import type { TermDetails, TermStatement } from "../../../api/content/ontologyApi";

/**
 * The simple term edits the browser proposes, and the triple patch each one is
 * (`docs/components/content.md` D-17). A patch names whole triples in full IRIs, so it
 * needs no prefixes; a replaced triple is removed exactly as the server reported it.
 */

export const RDFS_LABEL = "http://www.w3.org/2000/01/rdf-schema#label";
export const SKOS_DEFINITION = "http://www.w3.org/2004/02/skos/core#definition";
const RDF_TYPE = "http://www.w3.org/1999/02/22-rdf-syntax-ns#type";
const RDFS_SUBCLASS_OF = "http://www.w3.org/2000/01/rdf-schema#subClassOf";
const OWL_CLASS = "http://www.w3.org/2002/07/owl#Class";

export type TermEdit =
  | { readonly kind: "addLabel"; readonly text: string; readonly language: string | null }
  | { readonly kind: "renameLabel"; readonly statement: TermStatement; readonly text: string }
  /** Replaces `statement`, or adds a `skos:definition` when the term has none. */
  | {
      readonly kind: "editDefinition";
      readonly statement: TermStatement | null;
      readonly text: string;
      readonly language: string | null;
    }
  | {
      readonly kind: "addSubclass";
      readonly iri: string;
      readonly label: string;
      readonly language: string | null;
    };

export type TermEditKind = TermEdit["kind"];

export const TERM_EDIT_KINDS = Object.keys({
  addLabel: true,
  renameLabel: true,
  editDefinition: true,
  addSubclass: true,
} satisfies Record<TermEditKind, true>) as TermEditKind[];

export interface TriplePatch {
  readonly addTurtle: string;
  readonly removeTurtle: string;
}

/** The triples `edit` adds to and removes from the module, one statement per line. */
export function patchOfTermEdit(termIri: string, edit: TermEdit): TriplePatch {
  const term = turtleIri(termIri);
  switch (edit.kind) {
    case "addLabel":
      return { addTurtle: triple(term, RDFS_LABEL, turtleLiteral(edit.text, edit.language, null)), removeTurtle: "" };
    case "renameLabel":
      return replaced(term, edit.statement, edit.text);
    case "editDefinition":
      return edit.statement === null
        ? { addTurtle: triple(term, SKOS_DEFINITION, turtleLiteral(edit.text, edit.language, null)), removeTurtle: "" }
        : replaced(term, edit.statement, edit.text);
    case "addSubclass": {
      const child = turtleIri(edit.iri);
      return {
        addTurtle: [
          triple(child, RDF_TYPE, turtleIri(OWL_CLASS)),
          triple(child, RDFS_SUBCLASS_OF, term),
          triple(child, RDFS_LABEL, turtleLiteral(edit.label, edit.language, null)),
        ].join(""),
        removeTurtle: "",
      };
    }
  }
}

/**
 * The project's own module (`VersionRef`) the edit belongs in: one of `ownModules` that stores
 * the replaced statement, otherwise one that declares the term. `null` where only inherited
 * modules, such as the DMW basis's, hold them: the project can't change those (D-08, H-09).
 */
export function targetModuleOf(details: TermDetails, edit: TermEdit, ownModules: ReadonlySet<string>): string | null {
  const replacedStatement =
    edit.kind === "renameLabel" ? edit.statement : edit.kind === "editDefinition" ? edit.statement : null;
  const holders = replacedStatement?.modules ?? details.declaredIn;
  return holders.find((module) => ownModules.has(module)) ?? null;
}

function replaced(term: string, statement: TermStatement, text: string): TriplePatch {
  return {
    addTurtle: triple(term, statement.predicate, turtleLiteral(text, statement.language, statement.datatype)),
    removeTurtle: triple(
      term,
      statement.predicate,
      turtleLiteral(statement.text, statement.language, statement.datatype),
    ),
  };
}

function triple(subject: string, predicateIri: string, object: string): string {
  return `${subject} ${turtleIri(predicateIri)} ${object} .\n`;
}

const IRI_FORBIDDEN_CHARACTERS = new Set('<>"{}|^`\\');
const LAST_CONTROL_OR_SPACE = 0x20;

/** An absolute IRI as Turtle writes it, `<…>`. Throws for text that is no valid IRI. */
export function turtleIri(iri: string): string {
  if (!isAbsoluteIri(iri)) {
    throw new Error(`Not an absolute IRI: ${iri}`);
  }
  return `<${iri}>`;
}

/** Whether `text` can be written as an absolute IRI: a scheme, and no blanks or `<>"{}|^\``. */
export function isAbsoluteIri(text: string): boolean {
  return (
    /^[A-Za-z][A-Za-z0-9+.-]*:\S+$/u.test(text) &&
    Array.from(text).every(
      (character) => (character.codePointAt(0) ?? 0) > LAST_CONTROL_OR_SPACE && !IRI_FORBIDDEN_CHARACTERS.has(character),
    )
  );
}

/** A BCP 47 language tag in the form RDF accepts, such as `en` or `de-CH`. */
export function isLanguageTag(text: string): boolean {
  return /^[A-Za-z]{1,8}(-[A-Za-z0-9]{1,8})*$/u.test(text);
}

const STRING_ESCAPES: Readonly<Record<string, string>> = {
  '"': '\\"',
  "\\": "\\\\",
  "\n": "\\n",
  "\r": "\\r",
  "\t": "\\t",
};

/** A literal as Turtle writes it, with the escapes the canonical form uses. */
export function turtleLiteral(text: string, language: string | null, datatype: string | null): string {
  const escaped = Array.from(text, (character) => {
    const known = STRING_ESCAPES[character];
    if (known !== undefined) {
      return known;
    }
    const code = character.codePointAt(0) ?? 0;
    return code < 0x20 || code === 0x7f ? `\\u${code.toString(16).toUpperCase().padStart(4, "0")}` : character;
  }).join("");
  if (language !== null) {
    return `"${escaped}"@${language}`;
  }
  return datatype === null ? `"${escaped}"` : `"${escaped}"^^${turtleIri(datatype)}`;
}

/**
 * An IRI for a new term named `label`, in the namespace of `relativeTo` (everything up to
 * its last `#` or `/`): the label in UpperCamelCase, numbered if the IRI is taken.
 */
export function suggestedTermIri(relativeTo: string, label: string, taken: ReadonlySet<string>): string {
  const separator = Math.max(relativeTo.lastIndexOf("#"), relativeTo.lastIndexOf("/"));
  const namespace = separator >= 0 ? relativeTo.slice(0, separator + 1) : relativeTo;
  const words = label.normalize("NFKD").replace(/[̀-ͯ]/gu, "").match(/[A-Za-z0-9]+/gu) ?? [];
  const localName = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join("") || "NewClass";
  const start = /^[0-9]/u.test(localName) ? `C${localName}` : localName;
  let candidate = `${namespace}${start}`;
  for (let number = 2; taken.has(candidate); number += 1) {
    candidate = `${namespace}${start}${String(number)}`;
  }
  return candidate;
}
